import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, Search, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import ToolSidebar from '../components/ToolSidebar';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import axios from 'axios';
import '../styles/VisaStatusPage.css';
import '../styles/HomePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const STATUS_CONFIG = {
  approved: { icon: CheckCircle, color: '#4CAF50', bg: 'rgba(46,125,50,0.1)', label: 'Approved' },
  completed: { icon: CheckCircle, color: '#4CAF50', bg: 'rgba(46,125,50,0.1)', label: 'Completed' },
  pending: { icon: Clock, color: '#FF9800', bg: 'rgba(255,152,0,0.1)', label: 'Pending' },
  processing: { icon: Clock, color: '#2196F3', bg: 'rgba(33,150,243,0.1)', label: 'Processing' },
  rejected: { icon: XCircle, color: '#F44336', bg: 'rgba(244,67,54,0.1)', label: 'Rejected' },
};

const VisaStatusPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [appNumber, setAppNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const seoDefaults = {
    title: 'Check Visa Status Online — Track Your UAE Visa Application | Registan Tourism',
    description: 'Track your UAE visa application status online. Enter your application number to get real-time updates on your visa processing, approval, or rejection status with Registan Tourism.',
    keywords: 'visa status check, UAE visa tracking, visa application status, check visa online, Registan Tourism visa status, Dubai visa tracker'
  };
  const seo = useSEO('visa-status', seoDefaults);

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    const trimmed = appNumber.trim();
    if (!trimmed) return;
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/bookings/status/${encodeURIComponent(trimmed)}`);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No application found with this number. Please check and try again.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const key = (status || 'pending').toLowerCase();
    return STATUS_CONFIG[key] || { icon: AlertCircle, color: '#9E9E9E', bg: 'rgba(158,158,158,0.1)', label: status || 'Unknown' };
  };

  return (
    <div className="visa-status-page" data-testid="visa-status-page">
      <Helmet>
        <title>{seo?.title || seoDefaults.title}</title>
        <meta name="description" content={seo?.description || seoDefaults.description} />
        <meta name="keywords" content={seo?.keywords || seoDefaults.keywords} />
        <link rel="canonical" href="https://registantours.com/visa-status" />
        <meta property="og:title" content={seo?.title || seoDefaults.title} />
        <meta property="og:description" content={seo?.description || seoDefaults.description} />
        <meta property="og:url" content="https://registantours.com/visa-status" />
        <meta property="og:type" content="website" />
      </Helmet>

      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/visa-status" />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="visa-status-main tool-page-content">
          <div className="visa-status-hero">
            <h1 data-testid="visa-status-h1">Check Your Visa Application Status</h1>
            <h2 data-testid="visa-status-h2">Track Your UAE Visa Processing in Real Time</h2>
            <p className="visa-status-description">
              Enter your application number below to instantly check the current status of your visa application.
              Stay informed about your visa processing progress with Registan Tourism.
            </p>
          </div>

          <form className="visa-status-form" onSubmit={handleCheckStatus} data-testid="visa-status-form">
            <div className="visa-status-input-group">
              <input
                type="text"
                value={appNumber}
                onChange={(e) => setAppNumber(e.target.value.toUpperCase())}
                placeholder="Enter Application Number (e.g. JOHN20260001)"
                className="visa-status-input"
                data-testid="visa-status-input"
              />
              <button
                type="submit"
                className="visa-status-btn"
                disabled={loading || !appNumber.trim()}
                data-testid="visa-status-check-btn"
              >
                {loading ? (
                  <span className="visa-status-spinner" />
                ) : (
                  <><Search size={18} /> Check Status</>
                )}
              </button>
            </div>
          </form>

          {error && (
            <div className="visa-status-error" data-testid="visa-status-error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div className="visa-status-result" data-testid="visa-status-result">
              {(() => {
                const cfg = getStatusConfig(result.status);
                const StatusIcon = cfg.icon;
                return (
                  <>
                    <div className="visa-status-badge" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                      <StatusIcon size={22} />
                      <span>{cfg.label}</span>
                    </div>
                    <div className="visa-status-details">
                      <div className="visa-status-row">
                        <span className="visa-status-label">Application No.</span>
                        <span className="visa-status-value">{result.applicationNumber}</span>
                      </div>
                      <div className="visa-status-row">
                        <span className="visa-status-label">Applicant Name</span>
                        <span className="visa-status-value">{result.applicantName}</span>
                      </div>
                      <div className="visa-status-row">
                        <span className="visa-status-label">Service</span>
                        <span className="visa-status-value">{result.service}</span>
                      </div>
                      {result.lastUpdated && (
                        <div className="visa-status-row">
                          <span className="visa-status-label">Last Updated</span>
                          <span className="visa-status-value">{new Date(result.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </main>
      </div>

      <SiteFooter />
      <MobileBottomNav />
    </div>
  );
};

export default VisaStatusPage;
